import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Paper,
  Slide,
  Fade,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Article as ArticleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  DateRange as DateIcon,
  Link as LinkIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
  Science as ScienceIcon,
  LibraryBooks as LibraryBooksIcon,
  Bookmark as BookmarkIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import NavigationMenu from '../navigation/NavigationMenu';
import { APIPublishedWorkList, APIPublishedWorkDelete, APIPublishedWorkByAuthor } from '../../utilities/APIS';
import { headers, BASE_URL } from '../../utilities/Env';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import LoadingState from '../../components/LoadingState';

const PDFViewer = ({ open, onClose, pdfUrl, title = "PDF Document" }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { minHeight: '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, minHeight: '70vh' }}>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', minHeight: '70vh' }}
            title={title}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const MyPublications = () => {
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    if (userData?.id) {
      fetchPublications();
    }
  }, [userData]);

  const loadUser = () => {
    const user = sessionStorage.getItem('reseacher');
    if (user) {
      const jsonData = JSON.parse(user);
      setUserData(jsonData);
    }
  };

  const fetchPublications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${APIPublishedWorkByAuthor}/${userData.id}`, { headers });
      const data = Array.isArray(response.data.data) ? response.data.data : [];
      setPublications(data);
    } catch (error) {
      let errorMessage = "Failed to load publications";
      if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
      }
      setError(errorMessage);
      showToast("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = (fileName) => {
    if (fileName) {
      const pdfUrl = `${BASE_URL}${fileName}`;
      setSelectedPdfUrl(pdfUrl);
      setPdfDialogOpen(true);
    }
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
    setSelectedPdfUrl('');
  };

  const handleEdit = (publication) => {
    navigate("/submit-publications", { state: publication });
  };

  const handleDeleteClick = (publication) => {
    setSelectedPublication(publication);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPublication) return;
    
    setDeleting(true);
    try {
      const formData = new FormData();
      formData.append('update_id', selectedPublication.id);
      
      const response = await axios.post(APIPublishedWorkDelete, formData, { headers });
      
      if (response.data.status === "success") {
        showToast("Success", "Publication deleted successfully", "success");
        setPublications(prev => prev.filter(p => p.id !== selectedPublication.id));
        setDeleteDialogOpen(false);
        setSelectedPublication(null);
      } else {
        throw new Error(response.data.message || "Delete failed");
      }
    } catch (error) {
      let errorMessage = "Failed to delete publication";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
      }
      showToast("Error", errorMessage, "error");
    } finally {
      setDeleting(false);
    }
  };

  const getPublicationTypeIcon = (type) => {
    switch(type) {
      case 'journal': return <MenuBookIcon />;
      case 'conference': return <ScienceIcon />;
      case 'book': return <LibraryBooksIcon />;
      case 'chapter': return <BookmarkIcon />;
      case 'thesis': return <SchoolIcon />;
      default: return <ArticleIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'published': return 'success';
      case 'in_press': return 'warning';
      case 'submitted': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <>
        <NavigationMenu />
        <LoadingState />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <NavigationMenu />
      <Box component="main" sx={{ marginTop: 8, flexGrow: 1, p: 3 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Slide direction="down" in={true} mountOnEnter unmountOnExit>
            <Paper elevation={3} sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg, #1976d2 0%, #1565c0 100%)`,
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    My Publications
                  </Typography>
                  <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                    Manage your academic publications and research works
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    Total: {publications.length} publication{publications.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to="/submit-publications"
                  variant="contained"
                  color="secondary"
                  startIcon={<ArticleIcon />}
                >
                  Add New Publication
                </Button>
              </Box>
            </Paper>
          </Slide>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Publications Grid */}
          {publications.length === 0 ? (
            <Fade in={true}>
              <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                <ArticleIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No Publications Yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Start by adding your first academic publication
                </Typography>
                <Button
                  component={Link}
                  to="/submit-publications"
                  variant="contained"
                  color="primary"
                  startIcon={<ArticleIcon />}
                >
                  Add Publication
                </Button>
              </Paper>
            </Fade>
          ) : (
            <Grid container spacing={3}>
              {publications.map((pub, index) => (
                <Grid item xs={12} md={6} lg={4} key={pub.id}>
                  <Fade in={true} timeout={index * 100}>
                    <Card sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 3,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6
                      }
                    }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        {/* Header Chips */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          <Chip
                            icon={getPublicationTypeIcon(pub.publication_type)}
                            label={pub.publication_type}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            icon={<DateIcon />}
                            label={pub.publication_year}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={pub.status}
                            size="small"
                            color={getStatusColor(pub.status)}
                          />
                        </Box>

                        {/* Title */}
                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {pub.title}
                        </Typography>

                        {/* Authors */}
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {pub.author_name}
                        </Typography>

                        {/* Publisher */}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Publisher: {pub.publisher}
                        </Typography>

                        {/* DOI/ISSN */}
                        {(pub.doi || pub.issn) && (
                          <Box sx={{ mt: 1 }}>
                            {pub.doi && (
                              <Typography variant="caption" color="primary" display="block">
                                DOI: {pub.doi}
                              </Typography>
                            )}
                            {pub.issn && (
                              <Typography variant="caption" color="primary" display="block">
                                ISSN/ISBN: {pub.issn}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Abstract Preview */}
                        {pub.abstract && (
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            mt: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {pub.abstract}
                          </Typography>
                        )}
                      </CardContent>

                      <Divider />

                      <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {pub.file_name && (
                            <Tooltip title="View PDF">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleViewPdf(pub.file_name)}
                              >
                                <PdfIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {pub.url && (
                            <Tooltip title="View Online">
                              <IconButton 
                                size="small" 
                                color="info"
                                href={pub.url}
                                target="_blank"
                                component="a"
                              >
                                <LinkIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => handleEdit(pub)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteClick(pub)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardActions>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* PDF Viewer Dialog */}
      <PDFViewer
        open={pdfDialogOpen}
        onClose={handleClosePdfDialog}
        pdfUrl={selectedPdfUrl}
        title="Publication PDF"
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="h6">Delete Publication</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedPublication?.title}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyPublications;